import { useMemo } from 'react';
import Fuse from 'fuse.js';
import { RESTAURANT_SEARCH_SYNONYMS } from './resources/synonyms';

/**
 * Product search — relevance first, fuzzy only as a bounded fallback.
 *
 * `useFuzzySearch` on its own is too permissive for a supplier catalogue: with
 * `threshold: 0.5` + `ignoreLocation` a 2-4 letter query matches almost every
 * product name (measured against a real mixed catalogue: "se" hit 20 of 22
 * names), and those hits all score at the threshold, so Fuse's ranking collapses
 * into insertion order. The list then shows whichever supplier happens to sit
 * first in order history — for a wine-heavy restaurant, a page of wines — instead
 * of what was typed.
 *
 * This search requires EVERY query token to actually be found in the product
 * (exact token > prefix > substring), ranks by how well they matched with a
 * deterministic tie-break, and only falls back to Fuse — synonyms first, then a
 * hard score cutoff, capped at a handful of rows — when the strict pass is too
 * thin to be useful. So typos and salmon/zalm still work without flooding the list.
 *
 * Callers cap the rendered rows with `limit` and report the rest from
 * `totalMatches` ("showing first 200 of 412").
 */

export interface ProductSearchOptions<T> {
  data: T[];
  /** Flat object keys to search, e.g. ['name', 'searchableId']. */
  keys: string[];
  /** The (already debounced) query. Empty = browse mode, data passes through. */
  query: string;
  limit?: number;
  /** Below this many strict hits the synonym + fuzzy fallbacks kick in. */
  fuzzyFallbackMin?: number;
}

export interface ProductSearchResult<T> {
  /** Best matches first, capped at `limit`. */
  results: T[];
  /** How many matched in total, before the cap. */
  totalMatches: number;
}

const DEFAULT_LIMIT = 200;
const DEFAULT_FUZZY_FALLBACK_MIN = 5;

/**
 * A Fuse hit worse than this is noise rather than a typo. Calibrated against real
 * supplier names: a misspelled-but-correct product lands at 0.20-0.45, while
 * unrelated names start around 0.50 (every wine scored 0.50 for "sesam").
 */
const FUZZY_MAX_SCORE = 0.45;

/**
 * Below this length a query is a prefix the user is still typing, not a misspelling:
 * the strict pass already returns every product starting with it, and a fuzzy net
 * this wide only adds noise ("se" pulled in Sassicaia and Sancerre).
 */
const MIN_FUZZY_QUERY_LENGTH = 3;

/** Exact token match, then prefix, then substring — a found token is never free. */
const SCORE_EXACT = 1;
const SCORE_PREFIX = 0.9;
const SCORE_CONTAINS = 0.7;
/** Dutch compounds typed as one word: "sesamcrackers" against a "sesam" product token. */
const SCORE_COMPOUND = 0.6;

/** Lowercase, strip diacritics, collapse every non-alphanumeric run to one space. */
function normalizeText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function readKeys<T>(item: T, keys: string[]): string {
  return keys.map(key => String((item as any)?.[key] ?? '')).join(' ');
}

function bestTokenScore(queryToken: string, productTokens: string[]): number {
  let best = 0;
  for (const token of productTokens) {
    let score = 0;
    if (token === queryToken) score = SCORE_EXACT;
    else if (token.startsWith(queryToken)) score = SCORE_PREFIX;
    else if (queryToken.length >= 3 && token.includes(queryToken)) score = SCORE_CONTAINS;
    else if (token.length >= 4 && queryToken.startsWith(token)) score = SCORE_COMPOUND;
    if (score > best) best = score;
  }
  return best;
}

/**
 * Token match over the catalogue.
 *
 * `all` — every query token must be found in the product. This is the primary
 * pass: "sesam crackers" only matches something that is both.
 * `any` — at least one query token matched as a WHOLE token or a token prefix
 * (a mere substring is too weak to carry a match on its own). Recipe template
 * names are phrases ("verse limoen uitgeperst") that rarely match in full, so
 * this catches the product the phrase is about without opening the floodgates.
 *
 * Ranked by how many tokens matched and how well, the whole query appearing as
 * one phrase beating the same tokens scattered around, then shortest/alphabetical
 * name so the order is stable instead of "whatever order history happened to be in".
 */
function tokenPass<T>(data: T[], keys: string[], query: string, mode: 'all' | 'any'): T[] {
  const queryTokens = normalizeText(query).split(' ').filter(Boolean);
  if (queryTokens.length === 0) return [];
  const phrase = queryTokens.join(' ');

  const scored: { item: T; matched: number; score: number; text: string }[] = [];
  for (const item of data) {
    const text = normalizeText(readKeys(item, keys));
    if (!text) continue;
    const tokens = text.split(' ');

    let total = 0;
    let matched = 0;
    let strongMatch = false;
    for (const queryToken of queryTokens) {
      const score = bestTokenScore(queryToken, tokens);
      if (score === 0) {
        if (mode === 'all') { matched = 0; break; }
        continue;
      }
      if (score >= SCORE_PREFIX) strongMatch = true;
      matched += 1;
      total += score;
    }
    if (matched === 0) continue;
    if (mode === 'any' && !strongMatch) continue;

    scored.push({
      item,
      matched,
      score: total / queryTokens.length + (text.includes(phrase) ? 0.1 : 0),
      text,
    });
  }

  scored.sort((a, b) =>
    b.matched - a.matched ||
    b.score - a.score ||
    a.text.length - b.text.length ||
    a.text.localeCompare(b.text)
  );
  return scored.map(entry => entry.item);
}

/** Typo net: the old fuzzy engine, but score-gated and capped to `wanted` rows. */
function fuzzyPass<T>(data: T[], keys: string[], query: string, seen: Set<T>, wanted: number): T[] {
  if (wanted <= 0) return [];
  const fuse = new Fuse(data, {
    keys,
    threshold: 0.5,
    distance: 100,
    minMatchCharLength: 1,
    includeScore: true,
    ignoreLocation: true,
    isCaseSensitive: false,
    findAllMatches: true,
  });
  const hits: T[] = [];
  for (const hit of fuse.search(query)) {
    if ((hit.score ?? 1) > FUZZY_MAX_SCORE) continue;
    if (seen.has(hit.item)) continue;
    hits.push(hit.item);
    if (hits.length >= wanted) break;
  }
  return hits;
}

export function searchByRelevance<T>({
  data,
  keys,
  query,
  limit = DEFAULT_LIMIT,
  fuzzyFallbackMin = DEFAULT_FUZZY_FALLBACK_MIN,
}: ProductSearchOptions<T>): ProductSearchResult<T> {
  const trimmed = query.trim();
  if (!trimmed) return { results: data.slice(0, limit), totalMatches: data.length };

  const matches = tokenPass(data, keys, trimmed, 'all');
  const seen = new Set<T>(matches);
  const addAll = (items: T[]) => {
    for (const item of items) {
      if (seen.has(item)) continue;
      seen.add(item);
      matches.push(item);
    }
  };

  // Cross-language synonyms ("salmon" -> "zalm") go through the SAME token pass,
  // so a translated hit is a real hit and not a fuzzy near-miss.
  if (matches.length < fuzzyFallbackMin) {
    for (const synonym of RESTAURANT_SEARCH_SYNONYMS[trimmed.toLowerCase()] ?? []) {
      addAll(tokenPass(data, keys, synonym, 'all'));
      if (matches.length >= fuzzyFallbackMin) break;
    }
  }

  // Phrase queries (recipe template names) rarely match in full — accept products
  // that matched part of the phrase before falling back to fuzzy guessing.
  if (matches.length < fuzzyFallbackMin) {
    addAll(tokenPass(data, keys, trimmed, 'any'));
  }

  if (matches.length < fuzzyFallbackMin && trimmed.length >= MIN_FUZZY_QUERY_LENGTH) {
    fuzzyPass(data, keys, trimmed, seen, fuzzyFallbackMin - matches.length)
      .forEach(item => { seen.add(item); matches.push(item); });
  }

  return { results: matches.slice(0, limit), totalMatches: matches.length };
}

/**
 * Memoized {@link searchByRelevance}. `keys` is normally an inline array literal
 * (new identity every render), so the memo keys off its joined signature instead.
 */
export function useProductSearch<T>({
  data,
  keys,
  query,
  limit = DEFAULT_LIMIT,
  fuzzyFallbackMin = DEFAULT_FUZZY_FALLBACK_MIN,
}: ProductSearchOptions<T>): ProductSearchResult<T> {
  const keysSignature = keys.join('|');
  return useMemo(
    () => searchByRelevance({ data, keys: keysSignature.split('|'), query, limit, fuzzyFallbackMin }),
    [data, keysSignature, query, limit, fuzzyFallbackMin]
  );
}
